//@flow
import {ButtonGroup, Typography, makeStyles} from '@material-ui/core';
import React, {useState} from 'react';
import Tag from '../../../../elements/Tag/Tag';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {colors} from '../../../../assets/styles/colors';

import {
  TAX_INVOICE_STATE_LABELS,
  TAX_INVOICE_STATE_COLORS,
} from '../../../../entities';
import type {OrderTaxInvoiceData} from '../../../../entities';
import {Cancel, GetApp} from '@material-ui/icons';
import InlineLoader from '../../../../elements/LoadingSpinner/InlineLoader';
import {useCurrentOrderActions, useCurrentOrder} from '../../utils';
import {
  taxInvoiceCanBeCancelled,
  taxInvoiceCanCancelWithoutCreditNote,
} from '../../../tax-invoice/utils';
import ConfirmModal from '../../../../elements/Modals/ConfirmModal';

import {useToastActions} from '../../../toast-alert/utils';

import {formatDate} from '../../../../utils/date';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: '.5rem',
  },
  taxInvoiceCard: {
    display: 'flex',
    flexDirection: 'column',

    border: `1px solid ${colors.grey300}`,
    borderRadius: '.5rem',
    width: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '.75rem',
  },
  headerLabel: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 500,
    gap: '.5rem',
    fontSize: '.85rem',
  },
  divider: {
    height: '1px',
  },
  footer: {
    height: 44,
    borderTop: `dashed 2px ${colors.grey300}`,
  },
  cancelButton: {
    color: colors.error,
    '&[disabled]': {
      color: colors.grey500,
    },
  },
  downloadButton: {
    color: colors.primary,
    '&[disabled]': {
      color: colors.grey500,
    },
  },
  taxOptionsContainer: {
    display: 'flex',
    width: '100%',
    position: 'relative',
  },
  loader: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    background: 'rgba(255,255,255,0.5)',
    zIndex: 1,
    backdropFilter: 'blur(.25rem)',
  },
  subtitle: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 500,
    gap: '.5rem',
    fontSize: '.8rem',
    color: colors.grey600,
  },
  taxInvoiceData: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.25rem',
  },
}));

type Props = {
  taxInvoiceList: OrderTaxInvoiceData[],
  onRefetch?: () => void,
};

export default function InvoiceSection(props: Props) {
  const {taxInvoiceList, onRefetch} = props;
  const {order} = useCurrentOrder();
  const {cancelOrderTaxInvoice, loading} = useCurrentOrderActions();
  const [taxInvoiceToCancel, setTaxInvoiceToCancel] = useState<
    OrderTaxInvoiceData | typeof undefined
  >();
  const classes = useStyles();

  const {showToast} = useToastActions();

  const handleCancel = (taxInvoice: OrderTaxInvoiceData) => {
    const taxInvoiceId = taxInvoice.id;
    const canCancelWithoutCreditNote = taxInvoiceCanCancelWithoutCreditNote(
      taxInvoice
    );

    if (canCancelWithoutCreditNote) {
      return cancelOrderTaxInvoice(order?.id, taxInvoiceId).then(() => {
        if (onRefetch) onRefetch();
      });
    }

    setTaxInvoiceToCancel(taxInvoice);
  };

  const creditNoteList = taxInvoiceList.filter(
    taxInvoice => taxInvoice.taxInvoiceType.name === 'NOTA_CREDITO'
  );

  const taxInvoiceToCancelLabel =
    taxInvoiceToCancel?.taxInvoiceType.label || '';

  return (
    <aside className={classes.root}>
      {taxInvoiceList.map(taxInvoice => {
        const canCancel = taxInvoiceCanBeCancelled(taxInvoice, creditNoteList);

        return (
          <div key={taxInvoice.id} className={classes.taxInvoiceCard}>
            <header className={classes.header}>
              <div className={classes.taxInvoiceData}>
                <Typography className={classes.headerLabel}>
                  {taxInvoice.taxInvoiceType.label}: {taxInvoice.id}
                </Typography>
                {taxInvoice.formattedExternalReferenceId && (
                  <Typography className={classes.headerLabel}>
                    N° {taxInvoice.formattedExternalReferenceId}
                  </Typography>
                )}
                {taxInvoice.relatedTaxInvoiceId && (
                  <Typography className={classes.subtitle}>
                    Corresponde a: {taxInvoice.relatedTaxInvoiceId}
                  </Typography>
                )}

                {taxInvoice.issueDate && (
                  <Typography className={classes.subtitle}>
                    Registrado en:{' '}
                    {formatDate(taxInvoice.issueDate, 'MMMM D, YYYY h:mm A')}
                  </Typography>
                )}
              </div>
              <Tag
                label={TAX_INVOICE_STATE_LABELS[taxInvoice.state]}
                //$FlowFixMe
                type={TAX_INVOICE_STATE_COLORS[taxInvoice.state]}
              />
            </header>
            <div className={classes.taxOptionsContainer}>
              <InlineLoader
                size={20}
                className={classes.loader}
                loading={loading}
              />

              <TaxInvoiceCardOptionsByState
                pdfUrl={taxInvoice.pdfUrl}
                canCancel={canCancel}
                taxInvoice={taxInvoice}
                onCancel={() => {
                  handleCancel(taxInvoice);
                }}
              />
            </div>
          </div>
        );
      })}

      <ConfirmModal
        open={!!taxInvoiceToCancel}
        title={`¿Estás seguro que deseas cancelar esta ${taxInvoiceToCancelLabel}?`}
        message={`Para cancelar esta ${taxInvoiceToCancelLabel} se generará una Nota de Crédito. ¿Estás seguro que deseas cancelarla?`}
        onCancel={() => setTaxInvoiceToCancel(undefined)}
        onConfirm={() => {
          if (taxInvoiceToCancel) {
            cancelOrderTaxInvoice(order?.id, taxInvoiceToCancel.id)
              .then(() => {
                showToast({
                  title: 'La nota de crédito está en cola',
                  message: `En el transcurso del día se generará la nota de crédito correspondiente a la ${taxInvoiceToCancelLabel}`,
                  type: 'success',
                  duration: 15000,
                });
                if (onRefetch) onRefetch();
              })
              .catch(() => {
                showToast({
                  title: 'Error al generar la nota de crédito',
                  message: `No se pudo cancelar la ${taxInvoiceToCancelLabel}, revisa que no haya otra nota de crédito pendiente`,
                  type: 'danger',
                  duration: 15000,
                });
              })
              .finally(() => {
                setTaxInvoiceToCancel(undefined);
              });
          }
        }}
      />
    </aside>
  );
}

type TaxInvoiceCardOptionsByStateProps = {
  canCancel: boolean,
  pdfUrl?: string,
  onCancel: () => void,
};

function TaxInvoiceCardOptionsByState(
  props: TaxInvoiceCardOptionsByStateProps
) {
  const classes = useStyles();

  const {canCancel, onCancel, pdfUrl} = props;

  return (
    <ButtonGroup variant="text" fullWidth className={classes.footer}>
      <CustomButton
        startIcon={<Cancel />}
        fullWidth
        variant="text"
        color="danger"
        disabled={!canCancel}
        className={classes.cancelButton}
        onClick={onCancel}
      >
        Cancelar
      </CustomButton>
      <CustomButton
        startIcon={<GetApp />}
        fullWidth
        component="a"
        target="_blank"
        variant="text"
        disabled={!pdfUrl}
        className={classes.downloadButton}
        color="default"
        href={pdfUrl}
      >
        Descargar
      </CustomButton>
    </ButtonGroup>
  );
}
